import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import apiClient from 'helpers/apiService';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    faEdit,
    faPaperclip,
    faTrash
} from '@fortawesome/free-solid-svg-icons';

interface TaskItem {
    task_id: number;
    task_title: string;
    task_type: string;
    task_content: string;
    category: string;
    priority: string;
    task_duration: number;
    status: string;
}


const WorkLifeBalanceBox = ({ type }: { type: string }) => {

    const { t } = useTranslation();
    const [tasks, setTasks] = useState<TaskItem[] | null>(null);

    const [newTask, setNewTask] = useState<TaskItem>({
        task_id: 0,
        task_title: '',
        task_type: type,
        task_content: '',
        category: 'work', // default to work
        priority: 'normal', // default priority
        task_duration: 1, // default to 1 hour
        status: 'new'
    });

    const [activeFilter, setActiveFilter] = useState('all');

    const fetchTasks = async (category: string) => {
        try {
            const category_filter = category === 'ALL' ? '' : `&category=${category}`
            const response = await apiClient.get(`/v1/tasks?task_type=${type}${category_filter}`);
            setTasks(response.data);

        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const handleInputChange = (e: { target: { name: any; value: any; }; }) => {
        const { name, value } = e.target;
        setNewTask({
            ...newTask,
            [name]: name === 'task_duration' ? Math.max(0.25, parseFloat(value) || 0.25) : value
        });
    };

    const addTask = async () => {
        if (newTask.task_title.trim() === '') return;

        // Create new task with current timestamp as ID
        const task = {
            ...newTask,
            //id: Date.now(),
            createdAt: new Date().toISOString()
        };

        // Here you would typically also send this to your database
        const form = new FormData();
        form.append('taskForm', JSON.stringify(task));

        try {
            await apiClient.post('/v1/tasks/new', form,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    }
                }
            );
        } catch (error) {
            console.error('Request failed:', error);
        }

        /**
         * we use the functional form of state update:
         * If prevTasks exists, we spread its contents and add the new task.
         * If prevTasks is null, we initialize it with an array containing only the new task.
         */
        //setTasks(prevTasks => prevTasks ? [...prevTasks, newTask] : [newTask]);
        fetchTasks('ALL');

        // Reset the form
        setNewTask({
            task_id: 0,
            task_title: '',
            task_type: type,
            task_content: '',
            category: 'work',
            priority: 'normal',
            task_duration: 1,
            status: 'new'
        });
    };

    const toggleComplete = async (task_id: number) => {
        setTasks(prevTasks =>
            prevTasks
                ? prevTasks.map(task =>
                    task.task_id === task_id
                        ? {
                            ...task,
                            status: task.status === "completed" ? "new" : "completed",
                        }
                        : task
                )
                : null
        );

        // Get the new status
        const newStatus = tasks?.find(t => t.task_id === task_id)?.status === "completed" ? "new" : "completed";

        // Update task status in the backend
        try {
            await apiClient.put(`/v1/tasks/${task_id}/status/${newStatus}`);
        } catch (error) {
            console.error("Failed to update task status:", error);
        }
    };

    const deleteTask = async (task_id: number) => {
        setTasks(prevTasks => prevTasks ? prevTasks.filter(task => task.task_id !== task_id) : null);

        // Here you would delete the task from your database
        // deleteTaskFromDatabase(id);
        try {
            await apiClient.delete('/v1/tasks/' + task_id);
        } catch (error) {
            console.error("Failed to update task status:", error);
        }

        // refresh task list
        fetchTasks('ALL');

    };

    const filterTasks = () => {
        if (tasks === null)
            return []
        if (activeFilter === 'all') return tasks;
        if (activeFilter === 'work') return tasks.filter(task => task.category === 'work');
        if (activeFilter === 'life') return tasks.filter(task => task.category === 'life');
        if (activeFilter === 'urgent') return tasks.filter(task => task.priority === 'urgent');
        if (activeFilter === 'important') return tasks.filter(task => task.priority === 'important');

        return tasks;
    };

    // Get priority color
    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'urgent': return 'bg-danger-100 border-danger';
            case 'important': return 'bg-warning-100 border-warning';
            default: return 'bg-100 border-300';
        }
    };

    // Get category badge style
    const getCategoryBadge = (category: string) => {
        return category === 'work'
            ? 'bg-primary-500 text-white'
            : 'bg-success-500 text-white';
    };

    // Calculate total task_duration with proper decimal handling
    const calculateTotalHours = (category: string) => {

        let total = 0;
        if (category === 'all') {
            total = tasks ? tasks.reduce((sum, task) => sum + task.task_duration, 0) : 0;
        }
        else {
            total = tasks ? tasks
                .filter(task => category ? task.category === category : true)
                .reduce((sum, task) => sum + task.task_duration, 0) : 0;
        }

        // Format to preserve exact quarters (0.25, 0.5, 0.75)
        if (total % 0.25 === 0) {
            return total.toString();
        }
        return total.toFixed(2);

    };

    // Format task_duration display for individual tasks
    const formatHours = (task_duration: number) => {
        if (task_duration % 0.25 === 0) {
            return task_duration.toString();
        }
        return task_duration.toFixed(2);
    };

    useEffect(() => {
        fetchTasks("ALL");
    }, []);

    return (
        <>
            <h3 className="mb-2">
                {t("Work-Life Balance Manager")}
            </h3>


            {/* Add Task Form */}
            <div className="mb-6 p-4 border rounded-2 bg-soft">
                <h4 className="mb-3">Add New Task</h4>
                <div className="space-y-4">
                    <div>
                        <input
                            type="text"
                            name="task_title"
                            value={newTask.task_title}
                            onChange={handleInputChange}
                            placeholder="Task description"
                            className="w-100 p-2 border rounded"
                        />
                    </div>

                    <div className="d-flex">
                        <div className="d-flex  flex-grow-1 flex-column p-2 mb-2">
                            <label className="block text-sm font-medium mb-1">Category</label>
                            <select
                                name="category"
                                value={newTask.category}
                                onChange={handleInputChange}
                                className="w-100 p-2 border rounded"
                            >
                                <option value="work">Work</option>
                                <option value="life">Life</option>
                            </select>
                        </div>

                        <div className="d-flex  flex-grow-1 flex-column p-2 mb-2">
                            <label className="block text-sm font-medium mb-1">Priority</label>
                            <select
                                name="priority"
                                value={newTask.priority}
                                onChange={handleInputChange}
                                className="w-100 p-2 border rounded"
                            >
                                <option value="normal">Normal</option>
                                <option value="important">Important</option>
                                <option value="urgent">Urgent</option>
                            </select>
                        </div>

                        <div className="d-flex w-25 flex-column p-2 mb-2">
                            <label className="block text-sm font-medium mb-1">Hours</label>
                            <input
                                type="number"
                                name="task_duration"
                                min="0.25"
                                step="0.25"
                                value={newTask.task_duration}
                                onChange={handleInputChange}
                                className="w-100 p-2 border rounded"
                            />
                        </div>
                    </div>

                    <Button variant='primary'
                        onClick={addTask}
                        className="w-100 rounded"
                    >
                        Add Task
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="mb-4 d-flex flex-wrap gap-2">
                <button
                    onClick={() => setActiveFilter('all')}
                    className={`px-3 py-1 rounded border border-white ${activeFilter === 'all' ? 'bg-800 text-white' : 'bg-200'}`}
                >
                    All
                </button>
                <button
                    onClick={() => setActiveFilter('work')}
                    className={`px-3 py-1 rounded border border-white ${activeFilter === 'work' ? 'bg-primary-600 text-white' : 'bg-primary-100'}`}
                >
                    Work
                </button>
                <button
                    onClick={() => setActiveFilter('life')}
                    className={`px-3 py-1 rounded border border-white ${activeFilter === 'life' ? 'bg-success-600 text-white' : 'bg-success-100'}`}
                >
                    Life
                </button>
                <button
                    onClick={() => setActiveFilter('urgent')}
                    className={`px-3 py-1 rounded border border-white ${activeFilter === 'urgent' ? 'bg-danger-600 text-white' : 'bg-danger-100'}`}
                >
                    Urgent
                </button>
                <button
                    onClick={() => setActiveFilter('important')}
                    className={`px-3 py-1 rounded border border-white ${activeFilter === 'important' ? 'bg-warning-600 text-white' : 'bg-warning-100'}`}
                >
                    Important
                </button>
            </div>

            {/* Task List */}
            <div className="space-y-3">
                {filterTasks().length === 0 ? (
                    <p className="text-center text-gray-500 my-8">No tasks yet. Add your first task above!</p>
                ) : (
                    filterTasks().map(task => (
                        <div
                            key={task.task_id}
                            className={`p-2 border rounded d-flex align-items-center hover-actions-trigger border-bottom gap-2 mb-1 ${getPriorityColor(task.priority)}`}
                        >
                            <div className="flex-shrink-0 mx-2">
                                <input
                                    type="checkbox"
                                    checked={task.status === 'completed'}
                                    onChange={() => toggleComplete(task.task_id)}
                                    className=" my-0 align-self-start"
                                />
                            </div>

                            <div className="d-flex flex-column flex-grow-1">
                                <div className="d-flex justify-between">
                                    <p className={`mb-0 line-clamp-1 text-700 ${task.status === 'completed' ? 'text-decoration-line-through' : ''}`}>
                                        {task.task_title}
                                    </p>

                                </div>
                                <div className="d-flex gap-2 mt-1">
                                    <span style={{ minWidth: '50px', textAlign: 'center' }} className={`fs-9 px-2 py-1 rounded ${getCategoryBadge(task.category)}`}>
                                        {task.category}
                                    </span>
                                    {task.priority !== 'normal' && (
                                        <span className={` fs-9 px-2 py-1 rounded ${task.priority === 'urgent' ? 'bg-danger-500 text-white' : 'bg-warning-500 text-white'
                                            }`}>
                                            {task.priority}
                                        </span>
                                    )}
                                </div>

                            </div>
                            <div className="d-flex">
                                <span style={{ width: "40px" }}>{formatHours(task.task_duration)} {task.task_duration === 1 ? 'hr' : 'hrs'}</span>
                            </div>

                            <Button variant="phoenix-secondary" className="btn-icon fs-10" onClick={() => deleteTask(task.task_id)}>
                                <FontAwesomeIcon icon={faTrash} className="text-danger" />
                            </Button>



                        </div>
                    ))
                )}
            </div>


            {/* Summary */}
            <div className="mt-3 p-3 bg-100 rounded">
                <h4 className=" mb-1">Summary</h4>
                <div className="d-flex">
                    <div className='d-flex flex-column flex-grow-1'>
                        <p className="mb-0">Work Tasks: {tasks ? tasks.filter(t => t.category === 'work').length : 0} ({calculateTotalHours('work')} hrs)</p>
                        <p className="mb-0">Life Tasks: {tasks ? tasks.filter(t => t.category === 'life').length : 0} ({calculateTotalHours('life')} hrs)</p>
                    </div>
                    <div className='d-flex flex-column flex-grow-1'>
                        <p className="mb-0">Urgent: {tasks ? tasks.filter(t => t.priority === 'urgent').length : 0}</p>
                        <p className="mb-0">Important: {tasks ? tasks.filter(t => t.priority === 'important').length : 0}</p>
                        <p className="mb-0">Total Hours: {calculateTotalHours('all')} hrs</p>
                    </div>
                </div>
            </div>


        </>
    );
};

export default WorkLifeBalanceBox;

